import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./TemplateSetup.css";
import templateService from "../services/templateService";
import TemplateAIBuilder from "./TemplateAIBuilder";

const TemplateSetup = ({
  categories,
  templates = [],
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onApplyTemplate,
  isApplyingTemplate = false,
  isCreatingTemplate = false,
  updatingTemplate = {},
  deletingTemplate = {},
  onTemplatesChanged,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateTasks, setTemplateTasks] = useState([]);
  const [templateTodos, setTemplateTodos] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [draggedTaskIndex, setDraggedTaskIndex] = useState(null);
  const [selectedEditDay, setSelectedEditDay] = useState("monday");
  const [importTemplateId, setImportTemplateId] = useState("");
  const [editingTodoReminderIndex, setEditingTodoReminderIndex] = useState(null);
  const [reminderModalForm, setReminderModalForm] = useState({ time: "09:00", reminderMinutes: 0 });
  // Schedule image extraction state
  const [isExtractingSchedule, setIsExtractingSchedule] = useState(false);
  const [extractionMessage, setExtractionMessage] = useState(null); // { type: 'success'|'error', text: string }
  const [modalDirty, setModalDirty] = useState(false); // unsaved edits in the template modal
  const scheduleImageInputRef = useRef(null);

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0]._id);
    }
  }, [templates, selectedTemplateId]);

  const selectedTemplate = templates.find((t) => t._id === selectedTemplateId);

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setTemplateTasks([]);
    setTemplateTodos([]);
    setImportTemplateId("");
    setModalDirty(false);
    setShowModal(true);
  };

  const handleImportTasks = () => {
    if (!importTemplateId) return;

    const templateToImport = templates.find((t) => t._id === importTemplateId);
    if (!templateToImport) return;

    // Import all tasks from the selected template with defaults, strip _id
    const importedTasks = (templateToImport.tasks || []).map(({ _id, ...task }) => ({
      ...task,
      isAutomated: task.isAutomated || false,
      completionCount: task.completionCount || 0,
      addToCalendar: task.addToCalendar || false,
      reminderMinutes: task.reminderMinutes || 0,
      endsNextDay: task.endsNextDay || false,
    }));

    const importedTodos = (templateToImport.quickTodos || []).map(({ _id, ...todo }) => ({
      ...todo,
      deadlineOffsetDays: todo.deadlineOffsetDays || 0,
      reminderMinutes: todo.reminderMinutes || 0,
      reminderTime: todo.reminderTime || "09:00",
      _tempId: `todo_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    }));

    // Filter out tasks that already exist in current template (match name, category, day)
    const existingTaskKeys = new Set(templateTasks.map(t => `${t.name}|${t.category}|${t.day}`));
    const uniqueImportedTasks = importedTasks.filter(t => !existingTaskKeys.has(`${t.name}|${t.category}|${t.day}`));

    // Filter out todos that already exist in current template (match text, day)
    const existingTodoKeys = new Set(templateTodos.map(t => `${t.text}|${t.day}`));
    const uniqueImportedTodos = importedTodos.filter(t => !existingTodoKeys.has(`${t.text}|${t.day}`));

    if (uniqueImportedTasks.length < importedTasks.length || uniqueImportedTodos.length < importedTodos.length) {
      const skippedTasks = importedTasks.length - uniqueImportedTasks.length;
      const skippedTodos = importedTodos.length - uniqueImportedTodos.length;
      console.log(`Deduplication: Skipped ${skippedTasks} tasks and ${skippedTodos} todos that already existed.`);
    }

    // Add imported unique tasks to existing tasks
    setModalDirty(true);
    setTemplateTasks([...templateTasks, ...uniqueImportedTasks]);
    setTemplateTodos([...templateTodos, ...uniqueImportedTodos]);
    setImportTemplateId(""); // Reset selection
  };

  // ─── Schedule Image Extraction ────────────────────────────────────────────────

  /**
   * Compresses an image File to a canvas-scaled JPEG (max 1200px wide)
   * and returns { base64, mimeType }.
   */
  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const MAX = 1200;
          let { width, height } = img;
          if (width > MAX) {
            height = Math.round((height * MAX) / width);
            width = MAX;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          // Export as JPEG at 85% quality
          const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
          const base64 = dataUrl.split(",")[1];
          resolve({ base64, mimeType: "image/jpeg" });
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleScheduleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected if needed
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      setExtractionMessage({ type: "error", text: "Please upload an image file (JPEG, PNG, WEBP, etc.)" });
      return;
    }

    setIsExtractingSchedule(true);
    setExtractionMessage(null);

    try {
      const { base64, mimeType } = await compressImage(file);
      const result = await templateService.extractScheduleFromImage(base64, mimeType);

      const extracted = result.tasks || [];
      if (extracted.length === 0) {
        setExtractionMessage({ type: "error", text: "No schedule found in that image. Try a clearer photo of a timetable or schedule." });
        return;
      }

      // Assign default category and merge (deduplicate by name+day)
      const defaultCategory = categories.length > 0 ? categories[0].name : "General";
      const withCategory = extracted.map((t) => ({
        ...t,
        category: defaultCategory,
        endsNextDay: false,
      }));

      const existingKeys = new Set(templateTasks.map((t) => `${t.name.toLowerCase()}|${t.day}`));
      const newTasks = withCategory.filter((t) => !existingKeys.has(`${t.name.toLowerCase()}|${t.day}`));
      const skipped = withCategory.length - newTasks.length;

      setTemplateTasks((prev) => [...prev, ...newTasks]);
      setModalDirty(true);

      const msg = skipped > 0
        ? `✅ Added ${newTasks.length} task${newTasks.length !== 1 ? "s" : ""} (${skipped} duplicate${skipped !== 1 ? "s" : ""} skipped). Review and adjust categories below!`
        : `✅ Extracted ${newTasks.length} task${newTasks.length !== 1 ? "s" : ""} from your schedule! Review and adjust categories below.`;
      setExtractionMessage({ type: "success", text: msg });

      // Auto-switch to the day that has the most newly extracted tasks
      if (newTasks.length > 0) {
        const dayCounts = {};
        newTasks.forEach((t) => { dayCounts[t.day] = (dayCounts[t.day] || 0) + 1; });
        const topDay = Object.entries(dayCounts).sort((a, b) => b[1] - a[1])[0][0];
        setSelectedEditDay(topDay);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err.message || "Unknown error";
      setExtractionMessage({ type: "error", text: `❌ Extraction failed: ${serverMsg}` });
    } finally {
      setIsExtractingSchedule(false);
    }
  };


  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    // Ensure all tasks have isAutomated field, even if they don't in the database
    const tasksWithDefaults = (template.tasks || []).map((task) => ({
      ...task,
      isAutomated: task.isAutomated || false,
      completionCount: task.completionCount || 0,
      addToCalendar: task.addToCalendar || false,
      reminderMinutes: task.reminderMinutes || 0,
      endsNextDay: task.endsNextDay || false,
    }));
    setTemplateTasks(tasksWithDefaults);
    setTemplateTodos(
      (template.quickTodos || []).map((todo) => ({
        ...todo,
        deadlineOffsetDays: todo.deadlineOffsetDays || 0,
        reminderMinutes: todo.reminderMinutes || 0,
        reminderTime: todo.reminderTime || "09:00",
        _tempId: todo._id || `todo_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      })),
    );
    setModalDirty(false);
    setShowModal(true);
  };

  const addTask = () => {
    if (categories.length === 0) {
      alert("Please create at least one category first!");
      return;
    }
    setModalDirty(true);
    setTemplateTasks([
      ...templateTasks,
      {
        name: "",
        category: categories[0].name,
        day: selectedEditDay, // Add to currently selected day
        plannedTime: 0,
        isAutomated: false,
        scheduledStartTime: null,
        scheduledEndTime: null,
        endsNextDay: false,
        addToCalendar: false,
        reminderMinutes: 0,
      },
    ]);
  };

  const addTodo = () => {
    setModalDirty(true);
    setTemplateTodos([
      ...templateTodos,
      {
        _tempId: `todo_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        text: "",
        day: selectedEditDay,
        deadlineOffsetDays: 0,
        reminderMinutes: 0,
        reminderTime: "09:00",
      },
    ]);
  };

  const copyTasksFromDay = (sourceDay) => {
    if (!sourceDay) return;
    const sourceTasks = templateTasks.filter((t) => t.day === sourceDay);
    if (sourceTasks.length === 0) {
      alert(`No tasks found on ${sourceDay}!`);
      return;
    }
    const copiedTasks = sourceTasks.map(({ _id, ...task }) => ({
      ...task,
      day: selectedEditDay,
    }));

    // Deduplicate: Filter out tasks that already exist in the target day
    const existingTaskNames = new Set(
      templateTasks
        .filter((t) => t.day === selectedEditDay)
        .map((t) => `${t.name}|${t.category}`),
    );
    const uniqueCopiedTasks = copiedTasks.filter(
      (t) => !existingTaskNames.has(`${t.name}|${t.category}`),
    );

    if (uniqueCopiedTasks.length === 0 && copiedTasks.length > 0) {
      alert("All tasks from the source day already exist in the target day.");
      return;
    }

    setModalDirty(true);
    setTemplateTasks([...templateTasks, ...uniqueCopiedTasks]);
  };

  const copyTodosFromDay = (sourceDay) => {
    if (!sourceDay) return;
    const sourceTodos = templateTodos.filter((t) => t.day === sourceDay);
    if (sourceTodos.length === 0) {
      alert(`No quick todos found on ${sourceDay}!`);
      return;
    }
    const copiedTodos = sourceTodos.map(({ _id, _tempId, ...todo }) => ({
      ...todo,
      day: selectedEditDay,
      _tempId: `todo_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      reminderMinutes: todo.reminderMinutes || 0,
      reminderTime: todo.reminderTime || "09:00",
    }));

    // Deduplicate: Filter out todos that already exist in the target day
    const existingTodoTexts = new Set(
      templateTodos.filter((t) => t.day === selectedEditDay).map((t) => t.text),
    );
    const uniqueCopiedTodos = copiedTodos.filter(
      (t) => !existingTodoTexts.has(t.text),
    );

    if (uniqueCopiedTodos.length === 0 && copiedTodos.length > 0) {
      alert(
        "All quick todos from the source day already exist in the target day.",
      );
      return;
    }

    setModalDirty(true);
    setTemplateTodos([...templateTodos, ...uniqueCopiedTodos]);
  };

  // Functional updates: consecutive calls in one handler (e.g. reminder
  // modal Save sets time AND minutes) must not clobber each other.
  const updateTask = (index, field, value) => {
    setModalDirty(true);
    setTemplateTasks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTask = (index) => {
    setModalDirty(true);
    setTemplateTasks(templateTasks.filter((_, i) => i !== index));
  };

  const updateTodo = (index, field, value) => {
    setModalDirty(true);
    setTemplateTodos((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTodo = (index) => {
    setModalDirty(true);
    setTemplateTodos(templateTodos.filter((_, i) => i !== index));
  };

  const handleDragStart = (e, index) => {
    setDraggedTaskIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (targetIndex) => {
    if (draggedTaskIndex === null || draggedTaskIndex === targetIndex) return;

    const reordered = [...templateTasks];
    const [removed] = reordered.splice(draggedTaskIndex, 1);
    reordered.splice(targetIndex, 0, removed);

    setTemplateTasks(reordered);
    setDraggedTaskIndex(targetIndex); // Update index since item moved
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedTaskIndex(null);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      alert("Please provide a template name!");
      return;
    }

    if (templateTasks.length === 0 && templateTodos.length === 0) {
      alert("Please add at least one task or quick todo to your template!");
      return;
    }

    const validTasks = templateTasks.filter((t) => t.name.trim());
    // Strip UI-only fields before saving — backend doesn't need them
    const validTodos = templateTodos
      .filter((t) => t.text.trim())
      .map(({ _tempId, _showReminder, ...todo }) => todo);
    if (validTasks.length === 0 && validTodos.length === 0) {
      alert("Please provide names for your tasks or quick todos!");
      return;
    }

    // Google rejects events whose end is not after start ("time range is
    // empty"). Catch the typo at save time and name the task — unless the
    // task is explicitly marked as ending next day (overnight shift).
    const badRange = validTasks.find(
      (t) =>
        t.scheduledStartTime &&
        t.scheduledEndTime &&
        t.scheduledEndTime <= t.scheduledStartTime &&
        !t.endsNextDay,
    );
    if (badRange) {
      alert(
        `"${badRange.name}" (${badRange.day}): end time (${badRange.scheduledEndTime}) must be after start time (${badRange.scheduledStartTime}). Google Calendar rejects empty time ranges — or tick +1d if it truly ends the next day.`,
      );
      return;
    }

    console.log("Frontend - Saving template with tasks:", validTasks);
    console.log(
      "Frontend - isAutomated values:",
      validTasks.map((t) => ({ name: t.name, isAutomated: t.isAutomated })),
    );

    const templateData = {
      name: templateName,
      tasks: validTasks,
      quickTodos: validTodos,
    };

    try {
      if (editingTemplate) {
        await onUpdateTemplate(editingTemplate._id, templateData);
        setModalDirty(false);
        setShowModal(false);
      } else {
        await onCreateTemplate(templateData);
        setModalDirty(false);
        setShowModal(false);
      }
    } catch (err) {
      // Error handled by the parent
    };
  };

  const handleDelete = (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      onDeleteTemplate(templateId);
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null);
      }
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = categories.find((c) => c.name === categoryName);
    return category ? category.icon : "📋";
  };

  const getCategoryColor = (categoryName) => {
    const category = categories.find((c) => c.name === categoryName);
    return category ? category.color : "#6366f1";
  };

  const groupedTasks = selectedTemplate
    ? days.map((day) => ({
        day,
        tasks: selectedTemplate.tasks.filter((t) => t.day === day),
        quickTodos: (selectedTemplate.quickTodos || []).filter(
          (t) => t.day === day,
        ),
      }))
    : [];

  return (
    <div className="template-setup">
      <div className="template-header">
        <div>
          <h3>📅 Weekly Templates</h3>
          <p className="template-subtitle">
            {templates.length} template{templates.length !== 1 ? "s" : ""} saved
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          <button onClick={openNewTemplate} className="btn-new-template">
            + New Template
          </button>
          <TemplateAIBuilder
            categories={categories}
            selectedTemplate={selectedTemplate}
            onApplyTemplate={onApplyTemplate}
            onSaved={() => { if (onTemplatesChanged) onTemplatesChanged(); }}
          />
        </div>
      </div>

      {templates.length > 0 && (
        <>
          <div className="template-selector">
            <label>Select Template:</label>
            <div className="template-select-row">
              <select
                value={selectedTemplateId || ""}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="template-dropdown"
              >
                {templates.map((template) => (
                  <option key={template._id} value={template._id}>
                    {template.name} ({(template.tasks || []).length} tasks,{" "}
                    {(template.quickTodos || []).length} todos)
                  </option>
                ))}
              </select>
              {selectedTemplate && (
                <div className="template-actions-inline">
                  <button
                    onClick={() => openEditTemplate(selectedTemplate)}
                    className="btn-edit-small"
                    title="Edit template"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(selectedTemplate._id)}
                    className="btn-delete-small"
                    title="Delete template"
                    disabled={deletingTemplate[selectedTemplate._id]}
                  >
                    {deletingTemplate[selectedTemplate._id] ? "..." : "🗑️"}
                  </button>
                  <button
                    onClick={() => onApplyTemplate(selectedTemplate._id)}
                    className="btn-apply-template"
                    disabled={isApplyingTemplate}
                  >
                    {isApplyingTemplate ? "Applying..." : "🔄 Apply to Week"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {selectedTemplate && (
            <div className="template-preview">
              {groupedTasks.map(
                ({ day, tasks, quickTodos }) =>
                  (tasks.length > 0 || quickTodos.length > 0) && (
                    <div key={day} className="template-day">
                      <strong>
                        {day.charAt(0).toUpperCase() + day.slice(1)}:
                      </strong>
                      <div className="template-day-tasks">
                        {tasks.map((task, idx) => (
                          <span
                            key={idx}
                            className="template-task-badge"
                            style={{
                              borderColor: getCategoryColor(task.category),
                            }}
                            title={`${task.name}${task.scheduledStartTime ? ` • ${task.scheduledStartTime}${task.scheduledEndTime ? "-" + task.scheduledEndTime + (task.endsNextDay ? " (+1d)" : "") : ""}` : ""}${task.addToCalendar ? ` • 📅 calendar${task.reminderMinutes > 0 ? `, ⏰ ${task.reminderMinutes}m before` : ""}` : " • no calendar reminder"}`}
                          >
                            {getCategoryIcon(task.category)} {task.name}
                            {task.endsNextDay && task.scheduledEndTime && (
                              <span className="template-badge-cal" title="Ends next day">
                                {" "}+1d
                              </span>
                            )}
                            {task.addToCalendar && (
                              <span className="template-badge-cal" title={task.reminderMinutes > 0 ? `Reminder ${task.reminderMinutes} min before` : "Added to calendar, default reminder"}>
                                {" "}📅{task.reminderMinutes > 0 ? `${task.reminderMinutes}m` : ""}
                              </span>
                            )}
                          </span>
                        ))}
                        {quickTodos.map((todo, idx) => (
                          <span
                            key={`todo-${idx}`}
                            className="template-todo-badge"
                            title={`${todo.text}${todo.reminderMinutes > 0 ? ` • ⏰ ${todo.reminderMinutes}m before at ${todo.reminderTime || "09:00"}` : " • no reminder"}`}
                          >
                            ✓ {todo.text}
                            {todo.reminderMinutes > 0 && (
                              <span className="template-badge-cal">
                                {" "}⏰{todo.reminderMinutes}m
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ),
              )}
            </div>
          )}
        </>
      )}

      {templates.length === 0 && (
        <div className="no-templates">
          <p>No templates yet. Create your first weekly template!</p>
        </div>
      )}

      {showModal &&
        ReactDOM.createPortal(
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div
              className="modal-content large"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>
                  {editingTemplate ? "Edit Template" : "Create New Template"}
                  {modalDirty && (
                    <span className="template-dirty-dot" title="Unsaved changes — hit Update/Create to keep them">
                      {" "}• Unsaved
                    </span>
                  )}
                </h3>
                <p className="modal-description">
                  Define your fixed weekly schedule. You can apply this template
                  to any week!
                </p>

                <div className="form-group">
                  <label>Template Name</label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Work Week, Study Schedule, Gym Routine"
                  />
                </div>

                {/* Import from existing template - only show when creating new */}
                {!editingTemplate && templates.length > 0 && (
                  <div className="form-group import-section">
                    <label>
                      📋 Import Tasks from Existing Template (Optional)
                    </label>
                    <div className="import-row">
                      <select
                        value={importTemplateId}
                        onChange={(e) => setImportTemplateId(e.target.value)}
                        className="import-template-select"
                      >
                        <option value="">
                          -- Select a template to import --
                        </option>
                        {templates.map((template) => (
                          <option key={template._id} value={template._id}>
                            {template.name} ({template.tasks.length} tasks)
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleImportTasks}
                        disabled={!importTemplateId}
                        className="btn-import-tasks"
                        type="button"
                      >
                        Import Tasks
                      </button>
                    </div>
                    <p className="import-hint">
                      💡 Import tasks from another template to reuse common
                      tasks
                    </p>
                  </div>
                )}

                {/* ─── Schedule Image Extraction ─── */}
                <div className="schedule-image-upload-section">
                  <div className="schedule-upload-row">
                    <span className="schedule-upload-label">📷 Import from Schedule Photo</span>
                    <button
                      type="button"
                      className={`btn-upload-schedule${isExtractingSchedule ? " extracting" : ""}`}
                      onClick={() => scheduleImageInputRef.current?.click()}
                      disabled={isExtractingSchedule}
                      title="Upload a photo of your timetable or schedule and AI will extract all tasks automatically"
                    >
                      {isExtractingSchedule ? (
                        <><span className="extract-spinner" />Extracting…</>
                      ) : (
                        <>🔍 Scan Schedule Image</>
                      )}
                    </button>
                    <input
                      ref={scheduleImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleScheduleImageUpload}
                    />
                  </div>
                  {extractionMessage && (
                    <div className={`extraction-message extraction-message--${extractionMessage.type}`}>
                      {extractionMessage.text}
                      <button
                        type="button"
                        className="extraction-message-close"
                        onClick={() => setExtractionMessage(null)}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-body">
                {/* Day selector tabs */}
                <div className="day-tabs">
                  {days.map((day) => (
                    <button
                      key={day}
                      className={`day-tab ${selectedEditDay === day ? "active" : ""}`}
                      onClick={() => setSelectedEditDay(day)}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                      <span className="day-task-count">
                        ({templateTasks.filter((t) => t.day === day).length})
                      </span>
                    </button>
                  ))}
                </div>

                <div className="template-tasks-list">
                  <div className="tasks-header">
                    <h4>
                      {selectedEditDay.charAt(0).toUpperCase() +
                        selectedEditDay.slice(1)}{" "}
                      Tasks
                    </h4>
                    <div className="tasks-header-actions">
                      <div className="copy-from-day">
                        <select
                          className="copy-day-select"
                          defaultValue=""
                          onChange={(e) => {
                            copyTasksFromDay(e.target.value);
                            e.target.value = "";
                          }}
                        >
                          <option value="" disabled>Copy from…</option>
                          {days.filter((d) => d !== selectedEditDay).map((d) => (
                            <option key={d} value={d}>
                              {d.charAt(0).toUpperCase() + d.slice(1)} ({templateTasks.filter((t) => t.day === d).length})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button onClick={addTask} className="btn-add-task">
                        + Add Task
                      </button>
                    </div>
                  </div>

                  {templateTasks.filter((t) => t.day === selectedEditDay)
                    .length === 0 ? (
                    <p className="no-tasks">
                      No tasks for {selectedEditDay}. Add your first task!
                    </p>
                  ) : (
                    templateTasks.map((task, index) =>
                      task.day === selectedEditDay ? (
                        <div
                          key={index}
                          className={`template-task-row ${
                            draggedTaskIndex === index ? "dragging" : ""
                          }`}
                          draggable="true"
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDragEnter={() => handleDragEnter(index)}
                          onDrop={handleDrop}
                        >
                          <span
                            className="drag-handle-template"
                            title="Drag to reorder"
                          >
                            ⋮⋮
                          </span>
                          <input
                            type="text"
                            value={task.name}
                            onChange={(e) =>
                              updateTask(index, "name", e.target.value)
                            }
                            placeholder="Task name"
                            className="task-name-input"
                          />
                          <select
                            value={task.category}
                            onChange={(e) =>
                              updateTask(index, "category", e.target.value)
                            }
                            className="task-category-select"
                          >
                            {categories.map((cat) => (
                              <option key={cat._id} value={cat.name}>
                                {cat.icon} {cat.name}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            value={
                              task.plannedTime ? task.plannedTime / 60000 : ""
                            }
                            onChange={(e) =>
                              updateTask(
                                index,
                                "plannedTime",
                                (parseInt(e.target.value) || 0) * 60000,
                              )
                            }
                            placeholder="Min"
                            className="task-time-input"
                            min="0"
                          />
                          <label className="automated-checkbox-template">
                            <input
                              type="checkbox"
                              checked={task.isAutomated || false}
                              onChange={(e) =>
                                updateTask(
                                  index,
                                  "isAutomated",
                                  e.target.checked,
                                )
                              }
                            />
                            <span>🔄 Auto</span>
                          </label>
                          <label className="automated-checkbox-template calendar-toggle">
                            <input
                              type="checkbox"
                              checked={task.addToCalendar || false}
                              onChange={(e) =>
                                updateTask(
                                  index,
                                  "addToCalendar",
                                  e.target.checked,
                                )
                              }
                            />
                            <span>📅 Cal</span>
                          </label>
                          {task.addToCalendar && (
                            <select
                              value={task.reminderMinutes || 0}
                              onChange={(e) =>
                                updateTask(
                                  index,
                                  "reminderMinutes",
                                  parseInt(e.target.value),
                                )
                              }
                              className="task-reminder-select"
                              title="Reminder before task"
                            >
                              <option value={0}>No reminder</option>
                              <option value={5}>5 min</option>
                              <option value={10}>10 min</option>
                              <option value={15}>15 min</option>
                              <option value={30}>30 min</option>
                              <option value={60}>1 hour</option>
                            </select>
                          )}
                          {!task.scheduledStartTime && task.addToCalendar && (
                            <span
                              className="template-cal-fallback-hint"
                              title="No start time set — on apply, the calendar event defaults to 09:00"
                            >
                              ⏰→09:00
                            </span>
                          )}
                          <input
                            type="time"
                            value={task.scheduledStartTime || ""}
                            onChange={(e) =>
                              updateTask(
                                index,
                                "scheduledStartTime",
                                e.target.value,
                              )
                            }
                            className="task-time-slot-input"
                            title="Start time (optional)"
                          />
                          <span className="time-slot-separator-template">
                            →
                          </span>
                          <input
                            type="time"
                            value={task.scheduledEndTime || ""}
                            onChange={(e) =>
                              updateTask(
                                index,
                                "scheduledEndTime",
                                e.target.value,
                              )
                            }
                            className="task-time-slot-input"
                            title="End time (optional)"
                          />
                          <label
                            className="automated-checkbox-template next-day-toggle"
                            title="End time is on the next day (overnight shift, e.g. 22:00 → 06:00)"
                          >
                            <input
                              type="checkbox"
                              checked={task.endsNextDay || false}
                              onChange={(e) =>
                                updateTask(
                                  index,
                                  "endsNextDay",
                                  e.target.checked,
                                )
                              }
                            />
                            <span>+1d</span>
                          </label>
                          <button
                            onClick={() => removeTask(index)}
                            className="btn-remove-task"
                          >
                            ×
                          </button>
                        </div>
                      ) : null,
                    )
                  )}
                </div>

                <div className="template-todos-list">
                  <div className="tasks-header todo-header-row">
                    <h4>
                      {selectedEditDay.charAt(0).toUpperCase() +
                        selectedEditDay.slice(1)}{" "}
                      Quick Todos
                    </h4>
                    <div className="tasks-header-actions">
                      <div className="copy-from-day">
                        <select
                          className="copy-day-select"
                          defaultValue=""
                          onChange={(e) => {
                            copyTodosFromDay(e.target.value);
                            e.target.value = "";
                          }}
                        >
                          <option value="" disabled>Copy from…</option>
                          {days.filter((d) => d !== selectedEditDay).map((d) => (
                            <option key={d} value={d}>
                              {d.charAt(0).toUpperCase() + d.slice(1)} ({templateTodos.filter((t) => t.day === d).length})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={addTodo}
                        className="btn-add-task btn-add-todo"
                        type="button"
                      >
                        + Add Todo
                      </button>
                    </div>
                  </div>

                  {templateTodos.filter((todo) => todo.day === selectedEditDay)
                    .length === 0 ? (
                    <p className="no-tasks">
                      No quick todos for {selectedEditDay}. Add one for this
                      day!
                    </p>
                  ) : (
                    templateTodos.map((todo, index) =>
                      todo.day === selectedEditDay ? (
                        <div key={todo._tempId || todo._id || index} className="template-todo-row">
                          <input
                            type="text"
                            value={todo.text}
                            onChange={(e) =>
                              updateTodo(index, "text", e.target.value)
                            }
                            placeholder="Quick todo text"
                            className="todo-text-input"
                          />
                          <label className="todo-deadline-label">
                            +days
                            <input
                              type="number"
                              min="0"
                              value={todo.deadlineOffsetDays ?? 0}
                              onChange={(e) =>
                                updateTodo(
                                  index,
                                  "deadlineOffsetDays",
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              className="todo-deadline-offset-input"
                            />
                          </label>
                          <div className="todo-reminder-btn-wrap">
                            <button
                              type="button"
                              className={`todo-cal-icon-btn${(todo.reminderMinutes || 0) > 0 ? " active" : ""}`}
                              title={(todo.reminderMinutes || 0) > 0 ? `Reminder: ${todo.reminderTime || "09:00"} • ${todo.reminderMinutes < 60 ? todo.reminderMinutes + " min" : todo.reminderMinutes === 1440 ? "1 day" : todo.reminderMinutes / 60 + " hr"} before` : "Set reminder"}
                              onClick={() => {
                                setReminderModalForm({
                                  time: todo.reminderTime || "09:00",
                                  reminderMinutes: todo.reminderMinutes || 0,
                                });
                                setEditingTodoReminderIndex(index);
                              }}
                            >
                              📅
                            </button>
                          </div>
                          <button
                            onClick={() => removeTodo(index)}
                            className="btn-remove-task"
                            type="button"
                          >
                            ×
                          </button>
                        </div>
                      ) : null,
                    )
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-save"
                  disabled={
                    isCreatingTemplate ||
                    (editingTemplate && updatingTemplate[editingTemplate._id])
                  }
                >
                  {editingTemplate
                    ? updatingTemplate[editingTemplate._id]
                      ? "Updating..."
                      : "Update Template"
                    : isCreatingTemplate
                      ? "Creating..."
                      : "Create Template"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {editingTodoReminderIndex !== null && ReactDOM.createPortal(
        <div className="calendar-picker-overlay" onClick={() => setEditingTodoReminderIndex(null)}>
          <div className="calendar-reminder-picker" onClick={(e) => e.stopPropagation()}>
            <div className="calendar-picker-title">Set Time &amp; Reminder</div>
            <div className="calendar-picker-context">
              <div className="calendar-picker-context-label">Adding reminder for</div>
              <div className="calendar-picker-context-title">
                {templateTodos[editingTodoReminderIndex]?.text || "Quick todo"}
              </div>
              <div className="calendar-picker-context-meta">
                Scheduled for {selectedEditDay.charAt(0).toUpperCase() + selectedEditDay.slice(1)} (offset +{templateTodos[editingTodoReminderIndex]?.deadlineOffsetDays || 0} days)
              </div>
            </div>
            <div className="calendar-picker-grid" style={{ gridTemplateColumns: "1fr" }}>
              <label className="calendar-picker-field">
                <span>Time</span>
                <input
                  type="time"
                  value={reminderModalForm.time}
                  onChange={(e) => setReminderModalForm((p) => ({ ...p, time: e.target.value }))}
                />
              </label>
            </div>
            <label className="calendar-picker-field">
              <span>Reminder</span>
              <select
                value={reminderModalForm.reminderMinutes}
                onChange={(e) => setReminderModalForm((p) => ({ ...p, reminderMinutes: Number(e.target.value) }))}
              >
                <option value={0}>No reminder</option>
                <option value={5}>5 min before</option>
                <option value={10}>10 min before</option>
                <option value={15}>15 min before</option>
                <option value={30}>30 min before</option>
                <option value={60}>1 hour before</option>
                <option value={1440}>1 day before</option>
              </select>
            </label>
            <div className="calendar-picker-actions">
              <button className="btn-cancel" type="button" onClick={() => setEditingTodoReminderIndex(null)}>Cancel</button>
              <button
                className="btn-add"
                type="button"
                onClick={() => {
                  updateTodo(editingTodoReminderIndex, "reminderTime", reminderModalForm.time);
                  updateTodo(editingTodoReminderIndex, "reminderMinutes", reminderModalForm.reminderMinutes);
                  setEditingTodoReminderIndex(null);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TemplateSetup;
